import { clientWithoutAuth } from "@/axios";
import moment from "moment";
import utility from "@/utility";
import router from "@/router";
import {auth as firebaseAuth} from '../plugins/firebase';

export const auth = {
  namespaced: true,
  state: {
    accessToken: "",
    refreshToken: "",
    expiresIn: 0,
    type: null,
    roles: null,
    uid: null,
    accessTokenClaims: {},
    // iss
    // aud
    // jti
    // iat
    // nbf
    // exp
    // id
    processing: false,
    reAuthorizing: false,
    error: false,
    firebaseIdToken: "",
    firebaseRefreshToken: ""
  },
  mutations: {
    succeeded(state, { accessToken, refreshToken, expiresIn, type, roles, uid }) {
      const accessTokenClaims = JSON.parse(
        window.atob(accessToken.split(".")[1])
      );

      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.expiresIn = expiresIn;
      state.type = type;
      state.roles = roles;
      state.uid = uid;
      state.accessTokenClaims = { ...accessTokenClaims };
      state.processing = false;
      state.error = false;
    },
    failedLogin(state) {
      state.accessToken = "";
      state.refreshToken = "";
      state.expiresIn = 0;
      state.type = null;
      state.roles = null;
      state.uid = null;
      state.accessTokenClaims = {};
      state.processing = false;
      state.error = true;
      state.firebaseIdToken = "";
      state.firebaseRefreshToken = "";
    },
    failedRefresh(state) {
      state.accessToken = "";
      state.refreshToken = "";
      state.expiresIn = 0;
      state.type = null;
      state.roles = null;
      state.uid = null;
      state.accessTokenClaims = {};
      state.processing = false;
      state.error = false;
      state.firebaseIdToken = "";
      state.firebaseRefreshToken = "";
    },
    clear(state) {
      state.accessToken = "";
      state.refreshToken = "";
      state.expiresIn = 0;
      state.type = null;
      state.roles = null;
      state.uid = null;
      state.accessTokenClaims = {};
      state.processing = false;
      state.reAuthorizing = false;
      state.error = false;
      state.firebaseIdToken = "";
      state.firebaseRefreshToken = "";
      firebaseAuth.signOut();
    },
    processing(state) {
      state.processing = true;
    },
    reAuthorize(state) {
      state.reAuthorizing = true;
    },
    reAuthorized(state) {
      state.reAuthorizing = false;
    },
    firebaseAuth(state, {idToken, refreshToken}) {
      state.firebaseIdToken = idToken;
      state.firebaseRefreshToken = refreshToken;
    }
  },
  actions: {
    /**
     * tokenの有効状態チェック
     */
    validate({ commit, dispatch, getters }) {
      if (getters.authenticated) {
        if (getters.accessTokenIsExpired()) {
          // アクセストークン期限切れてる
          if (getters.canRefresh()) {
            // リフレッシュする
            return dispatch("refresh");
          } else {
            // リフレッシュトークンも期限切れてる
            commit("clear");
          }
        } else {
          // firebaseの方でも認証確認をする
          firebaseAuth.onAuthStateChanged((user) => {
            // 認証状態が切れていたらリフレッシュし、できなかった場合ログアウト
            if(!user && firebaseAuth.currentUser != null){
              firebaseAuth.currentUser.getIdToken().then((idToken) => {
              }).catch((e) => {
                commit("clear");
              });
            }else{
            }
          });
        }
      } else {
        // 認証されていない
        commit("clear");
      }

      return Promise.resolve();
    },
    /**
     * 認証
     */
    login({ commit }, { loginId }) {
      commit("processing");

      return new Promise((resolve, reject) => {
        clientWithoutAuth
          .post("/auth", {
            login_id: loginId
          })
          .then((response) => {
            commit("succeeded", {
              accessToken: response.data.accessToken,
              refreshToken: response.data.refreshToken,
              expiresIn: response.data.expiresIn,
              type: response.data.type,
              roles: response.data.roles,
              uid: response.data.uid
            });

            resolve();
          })
          .catch((error) => {
            let status = null;
            if (error.response) {
              status = error.response.status;
            } else {
              status = error.status || null;
            }

            if(status === 405) {
              utility
                .$alert("アカウントがロックされています。しばらく時間を置いて試してください。", "エラー", {
                  confirmButtonText: "OK",
                  type: "error",
                  center: true
                })
                .then(() => {});
            }
            commit("failedLogin");
            reject();
          });
      });
    },
    /**
     * tokenリフレッシュ
     */
    refresh({ commit, state }) {
      commit("processing");

      return new Promise((resolve) => {
        clientWithoutAuth
          .put("/auth", {
            refreshToken: state.refreshToken
          })
          .then((response) => {
            commit("succeeded", {
              accessToken: response.data.accessToken,
              refreshToken: response.data.refreshToken,
              expiresIn: response.data.expiresIn,
              type: response.data.type,
              roles: response.data.roles,
              uid: response.data.uid
            });

            resolve(response.data.accessToken);
          })
          .catch(() => {
            commit("failedRefresh");
            resolve("");
          });
      });
    },
    /**
     * ログアウト
     */
    logout({ commit, rootGetters }) {
      const logout = () => {
        commit("processing");

        clientWithoutAuth
          .delete("/auth")
          .then(() => {
            commit("clear");
            router.push({ name: "login" });
            location.reload();
          })
          .catch(() => {
            commit("clear");
            router.push({ name: "login" });
            location.reload();
          });
      };

      if (rootGetters["form/isAnyDirty"]) {
        utility
          .$confirm("このまま移動すると変更が保存されません。", "確認", {
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            type: "warning",
            center: true
          })
          .then(() => {
            commit("form/clearDirty", null, { root: true });
            logout();
          })
          .catch(() => {});
      } else {
        logout();
      }
    }
  },
  getters: {
    // 認証状態
    authenticated(state, getters) {
      return () => {
        if (state.accessToken) {
          if (getters["accessTokenIsExpired"]()) {
            if (getters["canRefresh"]()) {
              return true;
            }
          } else {
            return true;
          }
        }

        return false;
      };
    },
    // 認可(アクセス権限)
    authorized() {
      // TODO:
      return true;
    },
    accessToken(state) {
      return state.accessToken;
    },
    accessTokenIsExpired(state) {
      return () => state.accessTokenClaims.exp - 10 < moment().unix();
    },
    canRefresh(state) {
      return () => moment().unix() < state.expiresIn - 10;
    },
    processing(state) {
      return !!state.processing;
    },
    isError(state) {
      return !!state.error;
    },
    reAuthorizing(state) {
      return !!state.reAuthorizing;
    },
    accountId(state) {
      return state.accessTokenClaims.id || "";
    },
    isProvider(state) {
      return state.accessTokenClaims.type === 1;
    },
    isMaker(state) {
      return state.accessTokenClaims.type === 2;
    },
    makerId(state) {
      return state.accessTokenClaims.roles !== null ? JSON.parse(state.accessTokenClaims.roles)['makerId'] : null;
    },
    permissionAll(state) {
      return state.accessTokenClaims.roles !== null ? JSON.parse(state.accessTokenClaims.roles)['all'] > 0 : 0;
    },
    permissionMakerEdit(state) {
      return state.accessTokenClaims.roles !== null ? JSON.parse(state.accessTokenClaims.roles)['maker'] >= 2 : 0;
    },
    permissionMakerView(state) {
      return state.accessTokenClaims.roles !== null ? JSON.parse(state.accessTokenClaims.roles)['maker'] >= 1 : 0;
    },
    permissionUserEdit(state) {
      return state.accessTokenClaims.roles !== null ? JSON.parse(state.accessTokenClaims.roles)['user'] >= 2 : 0;
    },
    permissionUserView(state) {
      return state.accessTokenClaims.roles !== null ? JSON.parse(state.accessTokenClaims.roles)['user'] >= 1 : 0;
    },
    permissionArticleEdit(state) {
      return state.accessTokenClaims.roles !== null ? JSON.parse(state.accessTokenClaims.roles)['article'] >= 2 : 0;
    },
    permissionArticleView(state) {
      return state.accessTokenClaims.roles !== null ? JSON.parse(state.accessTokenClaims.roles)['article'] >= 1 : 0;
    },
    uid(state) {
      return state.accessTokenClaims.uid;
    },
    firebaseIdToken(state) {
      return state.firebaseIdToken;
    },
    firebaseRefreshToken(state) {
      return state.firebaseRefreshToken;
    }
  }
};
